import { Check, Hourglass, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useIsDesktop } from '@/core/hooks/useMediaQuery';
import { cn } from '@/shared/utils/cn';
import { Sheet, SheetContent, SheetTitle } from '@/shared/widgets/sheet';
import { Dialog, DialogContent, DialogTitle } from '@/shared/widgets/dialog';
import { Button } from '@/shared/widgets/button';
import { usePopupHistory } from '@/shared/hooks/usePopupHistory';
import {
  FormFieldProvider,
  FormSelectField,
  FormTextField,
  FORM_FIELD_BRAND_GREEN,
} from '@/shared/widgets/form';
import type { SizerService } from '../../application/services/SizerService';
import { useRingSizerForm } from '../hooks/useRingSizerForm';

interface RingSizerSheetProps {
  open: boolean;
  sizerService: SizerService;
  customizationId: number;
  category: string;
  onClose: () => void;
  onFreeSizerDetailsAdded?: () => void;
  onResendOtp?: () => void;
  onOtpVerified?: () => void;
}

const BRAND_ORANGE = '#f15d4c';

function formatResendTimer(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function InlineOtpButton({
  label,
  onClick,
  disabled = false,
  isLoading = false,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isLoading}
      className="flex h-full min-h-[36px] w-[80px] items-center justify-center whitespace-normal break-words border-0 bg-[#f15d4c] px-1 py-1 text-center text-[11px] font-medium uppercase leading-tight text-white hover:bg-[#f15d4c]/90 disabled:opacity-50"
    >
      {isLoading ? (
        <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
      ) : (
        label
      )}
    </button>
  );
}

interface RingSizerContentProps {
  isSubmitted: boolean;
  errorMessage: string;
  form: ReturnType<typeof useRingSizerForm>['form'];
  states: ReturnType<typeof useRingSizerForm>['states'];
  fieldErrors: ReturnType<typeof useRingSizerForm>['fieldErrors'];
  otpSent: boolean;
  showVerifyOtpField: boolean;
  otpVerified: boolean;
  resendCooldown: number;
  isSubmitting: boolean;
  isSendingOtp: boolean;
  isVerifyingOtp: boolean;
  otpMessage: string;
  otpMessageVariant: ReturnType<typeof useRingSizerForm>['otpMessageVariant'];
  canSendOtp: boolean;
  showSendOtpButton: boolean;
  showResendOtpButton: boolean;
  showResendTimer: boolean;
  updateField: ReturnType<typeof useRingSizerForm>['updateField'];
  validateContactFieldOnBlur: ReturnType<typeof useRingSizerForm>['validateContactFieldOnBlur'];
  lookupPincode: ReturnType<typeof useRingSizerForm>['lookupPincode'];
  sendOtp: ReturnType<typeof useRingSizerForm>['sendOtp'];
  verifyOtp: ReturnType<typeof useRingSizerForm>['verifyOtp'];
  submit: ReturnType<typeof useRingSizerForm>['submit'];
  onClose: () => void;
  TitleComponent: typeof SheetTitle | typeof DialogTitle;
  isDesktop: boolean;
}

function RingSizerContent({
  isSubmitted,
  errorMessage,
  form,
  states,
  fieldErrors,
  otpSent,
  showVerifyOtpField,
  otpVerified,
  resendCooldown,
  isSubmitting,
  isSendingOtp,
  isVerifyingOtp,
  otpMessage,
  otpMessageVariant,
  canSendOtp,
  showSendOtpButton,
  showResendOtpButton,
  showResendTimer,
  updateField,
  validateContactFieldOnBlur,
  lookupPincode,
  sendOtp,
  verifyOtp,
  submit,
  onClose,
  TitleComponent,
  isDesktop,
}: RingSizerContentProps) {
  if (isSubmitted) {
    return (
      <div className={cn('relative flex min-h-0 flex-1 flex-col bg-white', !isDesktop && 'rounded-t-lg')}>
        <button
          type="button"
          aria-label="Close ring sizer"
          className="absolute right-3 top-[17px] z-10 rounded-sm p-1 text-black opacity-80 hover:opacity-100"
          onClick={onClose}
        >
          <X className="h-[18px] w-[18px]" strokeWidth={1.75} />
        </button>
        <div className="flex flex-1 flex-col items-center justify-center px-[15px] py-16 text-center">
          <Check className="mb-[18px] mt-[27px] h-11 w-11 text-[#59ac59]" aria-hidden />
          <p className="font-secondary text-2xl font-semibold leading-normal text-black">
            Thank you for your request.
          </p>
          <p className="mt-2.5 text-base font-semibold tracking-[0.035em] text-black">
            You will receive the sizer in 5-7 days
          </p>
          <Button type="button" className="mt-14 w-full max-w-xs" onClick={onClose}>
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={cn('relative shrink-0 bg-white px-[15px] pb-[11px] pt-[17px] pr-10', !isDesktop && 'rounded-t-lg')}>
        <TitleComponent className="text-left text-[17px] font-semibold leading-normal tracking-[0.015em] text-black">
          Request for a Free Sizer
        </TitleComponent>
        <p className="text-left text-[14px] font-normal leading-normal tracking-[0.015em] text-black">
          Delivery in 5-7 days
        </p>
        <button
          type="button"
          aria-label="Close ring sizer"
          className="absolute right-3 top-[17px] rounded-sm p-1 text-black opacity-80 hover:opacity-100"
          onClick={onClose}
        >
          <X className="h-[18px] w-[18px]" strokeWidth={1.75} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-[15px] py-[10px]">
        <FormFieldProvider variant="floating">
          <div className="space-y-1">
            <FormTextField
              label="Name"
              value={form.name}
              onChange={(event) => updateField('name', event.target.value)}
              error={fieldErrors.name}
            />

            <FormTextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(event) => updateField('email', event.target.value)}
              onBlur={() => validateContactFieldOnBlur('email')}
              disabled={otpSent}
              error={fieldErrors.email}
            />

            <div className="relative">
              <FormTextField
                label="Mobile Number (Verification required)"
                type="tel"
                inputMode="numeric"
                value={form.mobile}
                onChange={(event) =>
                  updateField('mobile', event.target.value.replace(/\D/g, '').slice(0, 10))
                }
                maxLength={10}
                onBlur={() => validateContactFieldOnBlur('mobile')}
                disabled={otpSent}
                error={fieldErrors.mobile}
                endAdornment={
                  showSendOtpButton ? (
                    <InlineOtpButton
                      label="Send OTP"
                      onClick={() => void sendOtp()}
                      disabled={!canSendOtp || isSendingOtp}
                      isLoading={isSendingOtp}
                    />
                  ) : showResendOtpButton ? (
                    <InlineOtpButton
                      label="Resend OTP"
                      onClick={() => void sendOtp()}
                      disabled={!canSendOtp || isSendingOtp}
                      isLoading={isSendingOtp}
                    />
                  ) : undefined
                }
                footer={
                  showResendTimer ? (
                    <span className="absolute bottom-[2px] right-[6px] text-[11px] leading-none text-black">
                      Resend OTP in {formatResendTimer(resendCooldown)}
                      <Hourglass className="ml-0.5 inline h-3 w-3 align-[-2px]" aria-hidden />
                    </span>
                  ) : undefined
                }
              />
              {otpVerified && (
                <Check
                  className="absolute right-3 top-1/2 h-[26px] w-[26px] -translate-y-1/2 text-[#59ac59]"
                  aria-hidden
                />
              )}
            </div>

            {otpMessage && (
              <p
                className="m-[7px] text-[13px] leading-normal"
                style={{ color: otpMessageVariant === 'success' ? FORM_FIELD_BRAND_GREEN : BRAND_ORANGE }}
              >
                {otpMessage}
              </p>
            )}

            {showVerifyOtpField && !otpVerified && (
              <FormTextField
                label="OTP"
                type="tel"
                inputMode="numeric"
                maxLength={4}
                value={form.otp}
                onChange={(event) =>
                  updateField('otp', event.target.value.replace(/\D/g, '').slice(0, 4))
                }
                error={fieldErrors.otp}
                endAdornment={
                  <InlineOtpButton
                    label="Verify OTP"
                    onClick={() => void verifyOtp()}
                    disabled={!form.otp || isVerifyingOtp}
                    isLoading={isVerifyingOtp}
                  />
                }
              />
            )}

            <FormTextField
              label="Pincode"
              type="tel"
              inputMode="numeric"
              maxLength={6}
              value={form.pincode}
              onChange={(event) =>
                updateField('pincode', event.target.value.replace(/\D/g, '').slice(0, 6))
              }
              onBlur={() => {
                if (form.pincode.length === 6) void lookupPincode(form.pincode);
              }}
              error={fieldErrors.pincode}
            />

            <div className="grid grid-cols-2 gap-1">
              <FormTextField
                label="City"
                value={form.city}
                onChange={(event) => updateField('city', event.target.value)}
                error={fieldErrors.city}
              />
              <FormSelectField
                label="State"
                value={form.state}
                onChange={(event) => updateField('state', event.target.value)}
                options={states.map((state) => ({ value: state.name, label: state.name }))}
                error={fieldErrors.state}
              />
            </div>

            <FormTextField
              label="Address"
              value={form.address}
              onChange={(event) => updateField('address', event.target.value)}
              error={fieldErrors.address}
            />

            <FormTextField
              label="Street"
              value={form.street}
              onChange={(event) => updateField('street', event.target.value)}
              error={fieldErrors.street}
            />

            <div className="grid grid-cols-2 gap-1">
              <FormTextField
                label="Locality"
                value={form.locality}
                onChange={(event) => updateField('locality', event.target.value)}
                error={fieldErrors.locality}
              />
              <FormTextField
                label="Landmark (Optional)"
                required={false}
                value={form.landmark}
                onChange={(event) => updateField('landmark', event.target.value)}
              />
            </div>

            {errorMessage && (
              <p className="m-[7px] text-[13px] leading-normal text-red-600" role="alert">
                {errorMessage}
              </p>
            )}
          </div>
        </FormFieldProvider>
      </div>

      <div className="shrink-0 border-t border-[#e2e2e2] p-4">
        <Button
          type="button"
          className="w-full"
          onClick={() => void submit()}
          disabled={isSubmitting || !otpVerified}
          isLoading={isSubmitting}
        >
          Submit
        </Button>
      </div>
    </>
  );
}

export function RingSizerSheet({
  open,
  sizerService,
  customizationId,
  category: _category,
  onClose,
  onFreeSizerDetailsAdded,
  onResendOtp,
  onOtpVerified,
}: RingSizerSheetProps) {
  const {
    form,
    states,
    fieldErrors,
    otpSent,
    showVerifyOtpField,
    otpVerified,
    resendCooldown,
    isSubmitting,
    isSendingOtp,
    isVerifyingOtp,
    isSubmitted,
    errorMessage,
    otpMessage,
    otpMessageVariant,
    canSendOtp,
    updateField,
    validateContactFieldOnBlur,
    lookupPincode,
    sendOtp,
    verifyOtp,
    submit,
    reset,
  } = useRingSizerForm(sizerService, customizationId);

  const showSendOtpButton = !otpSent && !otpVerified && canSendOtp;
  const showResendOtpButton = otpSent && !otpVerified && resendCooldown === 0 && canSendOtp;
  const showResendTimer = otpSent && !otpVerified && resendCooldown > 0;

  useEffect(() => {
    if (isSubmitted) {
      onFreeSizerDetailsAdded?.();
    }
  }, [isSubmitted, onFreeSizerDetailsAdded]);

  const prevOtpVerifiedRef = useRef(false);
  useEffect(() => {
    if (otpVerified && !prevOtpVerifiedRef.current) {
      onOtpVerified?.();
    }
    prevOtpVerifiedRef.current = otpVerified;
  }, [otpVerified, onOtpVerified]);

  const handleSendOtp = async () => {
    if (otpSent) {
      onResendOtp?.();
    }
    await sendOtp();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  usePopupHistory({ isOpen: open, onClose: handleClose, stateKey: 'ringSizerSheet' });

  const isDesktop = useIsDesktop();

  const contentProps = {
    isSubmitted,
    errorMessage,
    form,
    states,
    fieldErrors,
    otpSent,
    showVerifyOtpField,
    otpVerified,
    resendCooldown,
    isSubmitting,
    isSendingOtp,
    isVerifyingOtp,
    otpMessage,
    otpMessageVariant,
    canSendOtp,
    showSendOtpButton,
    showResendOtpButton,
    showResendTimer,
    updateField,
    validateContactFieldOnBlur,
    lookupPincode,
    sendOtp: handleSendOtp,
    verifyOtp,
    submit,
    onClose: handleClose,
    isDesktop,
  };

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) handleClose(); }}>
        <DialogContent
          showCloseButton={false}
          className="flex max-h-[90vh] w-[calc(100%-2rem)] max-w-[520px] flex-col gap-0 overflow-hidden p-0"
        >
          <RingSizerContent {...contentProps} TitleComponent={DialogTitle} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(nextOpen) => { if (!nextOpen) handleClose(); }}>
      <SheetContent
        side="bottom"
        showCloseButton={false}
        className="flex max-h-[90dvh] flex-col gap-0 overflow-hidden p-0"
      >
        <RingSizerContent {...contentProps} TitleComponent={SheetTitle} />
      </SheetContent>
    </Sheet>
  );
}
